import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { GET_USER_BY_ID } from '@/Graphql/queries';
import { Role, Session, SessionStatus } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { Clock, MoreHorizontal } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatusBadge from '../StatusBadge/StatusBadge';



// Helper function to get initials
const getInitials = (firstName?: string | null, lastName?: string | null): string => {

  const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
  return firstInitial + lastInitial;
};

// Helper function to get image source based on session data
const getImageSource = (session: Session | null, startingSoon: boolean) => {
  if (startingSoon && session?.trainerIdUser?.profileImage) {
    return session.trainerIdUser.profileImage;
  }

  if (session?.programName === 'FitPrime') {
    return FitPrimeIcon;
  }

  if (session?.programName === 'FitVarsity') {
    return FitVarsityIcon;
  }

  if (session?.programName === 'FitCurve') {
    return FitCurveIcon;
  }

  // Default fallback
  return session?.trainingType?.icon || '';
};

type SessionCardProps = {
  session: Session | null;
  handleCancelClick: ({ id, programId, sportId }: { id: string, programId: string, sportId?: string }) => void;
  handleEditClick: (session: Session) => void;
  isSoon?: boolean;
  status?: string;
};

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  handleCancelClick,
  handleEditClick,
  isSoon,
  status,
}) => {

  const navigate = useNavigate();
  const { userDetails } = authState();

  const [startingSoon, setStartingSoon] = useState(false);
  const [remainingTime, setRemainingTime] = useState<string>('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // Extract and format the date and time
  const sessionStart = session?.sessionStartTime ? new Date(session.sessionStartTime) : null;
  const sessionEnd = session?.sessionEndTime ? new Date(session.sessionEndTime) : null;
  const isPastSession = sessionEnd && sessionEnd < new Date();

  const formattedStartDate = sessionStart ? format(sessionStart, 'EEE h:mm a') : '';
  const formattedEndDate = sessionEnd ? format(sessionEnd, 'h:mm a') : '';

  const { data: customerData } = useQuery(GET_USER_BY_ID, {
    variables: { id: session?.traineeId as string },
  });

  useEffect(() => {
    const checkTime = () => {
      const startTime = new Date(session?.sessionStartTime).getTime();
      const endTime = new Date(session?.sessionEndTime).getTime();
      const currentTime = new Date().getTime();
      const oneHourBeforeStart = startTime - 60 * 60 * 1000;
      const fifteenMinutesAfterEnd = endTime + 15 * 60 * 1000;

      if (currentTime >= oneHourBeforeStart && currentTime <= fifteenMinutesAfterEnd && isSoon) {
        setStartingSoon(true);
      } else {
        setStartingSoon(false);
      }
      if (currentTime >= startTime) {
        setRemainingTime('');
        return;
      }
      const timeDiff = startTime - currentTime;
      const hours = Math.floor(timeDiff / (1000 * 60 * 60))
        .toString()
        .padStart(2, '0');
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
        .toString()
        .padStart(2, '0');
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)
        .toString()
        .padStart(2, '0');
      setRemainingTime(`${hours}:${minutes}:${seconds}`);
    };

    checkTime();
    const interval = setInterval(checkTime, 1000); // Check every minute

    return () => clearInterval(interval);
  }, [session?.sessionStartTime]);
  return (
    <Card
      className='border-none mt-4'
      style={{
        background: startingSoon ? 'linear-gradient(90deg, #0f1129, #1c1d35)' : 'rgb(25, 27, 47)',
      }}
    >
      {/* Mobile View */}
      <div
        className='p-4 rounded-xl flex flex-col gap-4 w-full md:hidden bg-[#151525]'
        style={{
          background: startingSoon
            ? 'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%), linear-gradient(173.47deg, rgba(211, 138, 69, 0) 32.21%, rgba(211, 185, 69, 0.1) 94.86%)'
            : 'rgb(25, 27, 47)',
          borderWidth: startingSoon ? '0px 1px 1px 1px' : '0px',
          borderStyle: startingSoon ? 'solid' : 'none',
          borderImageSource: startingSoon
            ? 'linear-gradient(179.55deg, rgba(240, 231, 113, 0) 15.61%, rgba(238, 211, 96, 0.521698) 62.24%, rgba(255, 164, 76, 0.34) 99.61%)'
            : 'none',
          borderImageSlice: startingSoon ? 1 : 0,
          borderRadius: startingSoon ? '8px 8px 8px 8px' : '8px',
        }}
      >
        <div className='flex flex-row items-center gap-4'>
          <div className='flex justify-between items-start w-fit'>
            <div className='bg-[#12407D] flex flex-col items-center justify-center rounded-full h-14 w-14'>
              <span className='text-xs text-white uppercase'>
                {sessionStart ? format(sessionStart, 'MMM') : ''}
              </span>
              <span className='text-lg text-white font-bold'>
                {new Date(session?.sessionStartTime).getDate()}
              </span>
            </div>
          </div>

          <div className='flex items-center gap-3'>
            <div className='w-12 h-12 rounded-full overflow-hidden bg-gray-600'>
              {userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach ? (
                session?.traineeIdUser?.profileImage ? (
                  <img
                    src={session?.traineeIdUser?.profileImage || ''}
                    alt='trainer'
                    width={session?.traineeIdUser?.profileImage ? '100%' : 32}
                    height={session?.traineeIdUser?.profileImage ? '100%' : 32}
                    className='object-cover'
                  />
                ) : (
                  <div className='flex items-center justify-center w-full h-full text-xl font-semibold'>
                    {getInitials(session?.traineeIdUser?.firstName, session?.traineeIdUser?.lastName)}
                  </div>
                )
              ) : (

                <img
                  src={getImageSource(session, startingSoon)}
                  alt={
                    session?.trainerIdUser?.firstName
                      ? session.trainerIdUser.firstName.charAt(0)
                      : 'I'
                  }
                  width={startingSoon && session?.trainerIdUser?.profileImage ? '100%' : 36}
                  height={startingSoon && session?.trainerIdUser?.profileImage ? '100%' : 36}
                  className='object-cover'
                />
              )}
            </div>
            <div className='flex flex-col gap-2'>
              <div className='text-xs text-white'>#{session?.sessionUniqId}</div>
              <div className='flex flex-wrap items-center gap-1'>
                <span className='text-white font-medium'>{session?.programName === 'FitPrime' ? session?.programName + `(${session?.trainingType?.title})` : session?.programName}</span>
                {startingSoon && session?.trainerId && (
                  <>
                    <span className='text-white'>with</span>
                    <span className='text-white'>
                      {userDetails?.role === Role.Trainee
                        ? session?.trainerIdUser?.firstName
                        : customerData?.getUserById?.firstName}
                    </span>
                  </>
                )}
              </div>
              <div className='flex md:hidden items-center gap-2 text-sm text-gray-300'>
                <Clock className='h-4 w-4' />
                <span>
                  {formattedStartDate} - {formattedEndDate}
                </span>
              </div>
              {startingSoon && session?.sessionStatus !== SessionStatus.Open ? (
                ''
              ) : (
                <div className='flex items-center gap-2'>
                  {session?.sessionStatus == SessionStatus.Completed &&
                    ((userDetails?.role === Role.Trainee &&
                      session.trainerExpertiseRating == null) ||
                      ((userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach) &&
                        session.assesment?.assessment == null)) && (
                      <div className='flex items-center gap-2 mt-1'>
                        <Link
                          id='add-review'
                          to={`/feedback/${session?._id}`}
                          className='flex items-center text-sm px-3 py-0 text-white bg-bgSecondary rounded-full h-8'
                        >
                          + Add Review
                        </Link>
                      </div>
                    )}
                  <div className='w-min'>{status && <StatusBadge status={status} />}</div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='hidden md:flex items-center gap-2 text-sm text-gray-300'>
          <Clock className='h-4 w-4' />
          <span>
            {formattedStartDate} - {formattedEndDate}
          </span>
        </div>
        {startingSoon &&
          (session?.sessionStatus === SessionStatus.Open ||
            session?.sessionStatus === SessionStatus.Noshow) && (
            <Button
              id='join-btn'
              variant={'ghost'}
              className='w-full bg-white text-black hover:bg-gray-200'
              size='lg'
              onClick={() => navigate(`/meeting/${session?._id}`)}
            >
              Join
            </Button>
          )}

        {startingSoon && (
          <div className='text-center text-sm text-gray-400'>
            {remainingTime ? `Starts in ${remainingTime}` : 'In Progress'}
          </div>
        )}
      </div>

      {/* Desktop Layout */}
      <div
        className='p-[1px] rounded-xl'
        style={{
          background: startingSoon
            ? 'linear-gradient(179.55deg, rgba(240, 231, 113, 0) 15.61%, rgba(238, 211, 96, 0.521698) 62.24%, rgba(255, 164, 76, 0.34) 99.61%)'
            : '',
        }}
      >
        <div
          className='hidden md:flex p-4 items-center gap-4 rounded-xl'
          style={{
            background: startingSoon
              ? 'linear-gradient(to top, #151525, #272428)'
              : 'rgb(25, 27, 47)',
          }}
        >
          <div className='flex flex-col bg-[#12407D] items-center justify-center rounded-full w-16 h-16'>
            <span className='text-[#B8D4FF] text-sm font-semibold'>
              {sessionStart ? format(sessionStart, 'MMM') : ''}
            </span>
            <span className='leading-5 text-base text-white font-semibold'>
              {new Date(session?.sessionStartTime).getDate()}
            </span>
          </div>

          <div className='flex items-center gap-3 flex-1'>
            <div className='flex items-center justify-center w-16 h-16 rounded-full overflow-hidden bg-[#2A2D36]'>
              {userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach ? (
                session?.traineeIdUser?.profileImage ? (
                  <img
                    src={session?.traineeIdUser?.profileImage || ''}
                    alt='trainer'
                    width={session?.traineeIdUser?.profileImage ? '100%' : 32}
                    height={session?.traineeIdUser?.profileImage ? '100%' : 32}
                    className='object-cover'
                  />
                ) : (
                  <div className='flex items-center justify-center w-full h-full text-gray-600 text-3xl font-semibold'>
                    {getInitials(session?.traineeIdUser?.firstName, session?.traineeIdUser?.lastName)}
                  </div>
                )
              ) : (

                <img
                  src={getImageSource(session, startingSoon)}
                  alt={
                    session?.trainerIdUser?.firstName
                      ? session.trainerIdUser.firstName.charAt(0)
                      : 'I'
                  }
                  width={startingSoon && session?.trainerIdUser?.profileImage ? '100%' : 36}
                  height={startingSoon && session?.trainerIdUser?.profileImage ? '100%' : 36}
                  className='object-cover'
                />
              )}
            </div>
            <div className='flex flex-col'>
              <div className='text-xs font-normal text-[#A8ADB9]'>#{session?.sessionUniqId}</div>
              <div className='flex items-center gap-1 text-[#FFFFFF]'>
                <span className='text-white font-medium'>{session?.programName === 'FitPrime' ? session?.programName + `(${session?.trainingType?.title})` : session?.programName}</span>
                {userDetails?.role == Role.Trainee && session?.trainerId ? (
                  <>
                    <span>with</span>
                    <span className='font-semibold'>{session?.trainerIdUser?.firstName}</span>
                  </>
                ) : userDetails?.role == Role.Trainer || userDetails?.role == Role.Coach ? (
                  <>
                    <span>with</span>
                    <span className='font-semibold'>{customerData?.getUserById?.firstName}</span>
                  </>
                ) : (
                  ''
                )}
              </div>
              {session?.sessionStatus == SessionStatus.Completed &&
                ((userDetails?.role === Role.Trainee && session.trainerExpertiseRating == null) ||
                  ((userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach) &&
                    session.assesment?.assessment == null)) && (
                  <div className='flex items-center gap-2 mt-1'>
                    <Link
                      id='add-review'
                      to={`/feedback/${session?._id}`}
                      className='flex items-center text-sm px-3 py-0 text-white bg-bgSecondary rounded-full h-8'
                    >
                      + Add Review
                    </Link>
                  </div>
                )}
            </div>
          </div>

          <div className='flex flex-col items-end text-sm text-[#FFFFFF]'>
            <div className='flex items-center gap-2'>
              <Clock className='h-4 w-4' />
              <span>
                {formattedStartDate} - {formattedEndDate}
              </span>
            </div>
            {startingSoon && (
              <div>{remainingTime ? `Starts in ${remainingTime}` : 'In Progress'}</div>
            )}
          </div>

          <div className='flex items-center gap-2'>
            {startingSoon &&
              (session?.sessionStatus === SessionStatus.Open ||
                session?.sessionStatus === SessionStatus.Noshow ||
                session?.sessionStatus === 'Scheduled') &&
              !status ? (
              <Button
                id='join-btn'
                className=' text-black hover:bg-gray-200'
                style={{ background: '#FFFFFF' }}
                size='sm'
                onClick={() => navigate(`/session/${session?._id}`)}
              >
                Join
              </Button>
            ) : (
              <StatusBadge status={status} />
            )}
            {(status === 'Completed' || (!isPastSession && status !== 'Canceled')) && (
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id='more-btn'
                    variant='ghost'
                    size='icon'
                    className='text-gray-400 hover:text-white'
                  >
                    <MoreHorizontal className='h-5 w-5' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align='center' className='p-2 w-40 border-none bg-gray-800'>
                  <ul className='text-sm text-white rounded-sm'>
                    <li
                      className='p-2 hover:bg-zinc-900 cursor-pointer rounded-sm'
                      onClick={() => {
                        navigate(`/session/${session?._id}`);
                        setIsPopoverOpen(false); // Close the popover
                      }}
                    >
                      View
                    </li>
                    {sessionStart &&
                      sessionStart.getTime() - new Date().getTime() > 24 * 60 * 60 * 1000 &&
                      userDetails?.role !== Role.Trainer &&
                      userDetails?.role !== Role.Coach && (
                        <li
                          className='p-2 hover:bg-zinc-900 cursor-pointer rounded-sm'
                          onClick={() => {
                            session && handleEditClick(session);
                            setIsPopoverOpen(false); // Close the popover
                          }}
                        >
                          Edit
                        </li>
                      )}
                    {userDetails?.role === Role.Trainee &&
                      !startingSoon &&
                      sessionStart &&
                      sessionStart?.getTime() - new Date().getTime() > 12 * 60 * 60 * 1000 && (
                        <li
                          className='p-2 hover:bg-zinc-900 cursor-pointer rounded-sm'
                          onClick={() => {
                            if (session?._id && session?.programId) {
                              handleCancelClick({ id: session._id, programId: session.programId, ...(session?.sportId && { sportId: session.sportId }) });
                            }
                            setIsPopoverOpen(false); // Close the popover
                          }}
                        >
                          Cancel
                        </li>
                      )}
                  </ul>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SessionCard;
